<template>
    <div class="setup-container">
        <div class="page-hero">
            <div>
                <p class="eyebrow">Permissions</p>
                <h1>Create Route</h1>
                <p class="hero-copy">Define dashboard routes and keep the route tree easy to manage.</p>
            </div>
            <button class="setup-btn" @click="loadRoutes">
                <RefreshCcw class="icon" />
                Refresh
            </button>
        </div>

        <section class="panel">
            <form class="setup-form" @submit.prevent="submitForm">

                <div class="form-group">
                    <label for="routeName">Route name</label>
                    <input id="routeName" v-model="form.routeName" type="text" placeholder="Enter route name" required />
                </div>

                <div class="form-group">
                    <label for="routePath">Route path</label>
                    <input id="routePath" v-model="form.routePath" type="text" placeholder="/example-route" required />
                </div>

                <div class="buttons mt-3">
                    <button type="submit">Create Route</button> &nbsp;
                    <button type="button" @click="resetForm">Reset</button>
                </div>
            </form>
        </section>

        <section class="panel">
            <div class="header-search">
                <div>
                    <p class="section-kicker">Route list</p>
                    <h2>Registered routes</h2>
                </div>
                <div class="search-bar">
                    <Search />
                    <input v-model="searchQuery" type="text" placeholder="Search routes..." />
                </div>
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Route</th>
                        <th>Path</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="paginatedRoutes.length === 0">
                        <td colspan="6" class="no-data">No routes found.</td>
                    </tr>
                    <tr v-else v-for="(item, index) in paginatedRoutes" :key="item.RouteId ?? index">
                        <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td>{{ item.RouteName }}</td>
                        <td>{{ item.RoutePath }}</td>
                    </tr>
                </tbody>
            </table>

            <div class="pagination">
                <button @click="prevPage" :disabled="currentPage === 1">
                    <ChevronLeft />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="{ active: currentPage === page }"
                >
                    {{ page }}
                </button>
                <button @click="nextPage" :disabled="currentPage === totalPages">
                    <ChevronRight />
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ChevronLeft, ChevronRight, RefreshCcw, Search } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";

const apiListUrl = "/routes_lists.php";
const apiCreateUrl = "/routes_setup.php";

const routes = ref([]);
const searchQuery = ref("");
const currentPage = ref(1);
const pageSize = 8;

const form = ref({
    routeName: "",
    routePath: "",
    parentRouteId: "",
    sortOrder: ""
});

const filteredRoutes = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return routes.value;

    return routes.value.filter((item) => {
        return (
            String(item.RouteName || "").toLowerCase().includes(query) ||
            String(item.RoutePath || "").toLowerCase().includes(query)
        );
    });
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredRoutes.value.length / pageSize)));

const paginatedRoutes = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredRoutes.value.slice(start, start + pageSize);
});

watch(searchQuery, () => {
    currentPage.value = 1;
});

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) currentPage.value = page;
}

function prevPage() {
    if (currentPage.value > 1) currentPage.value--;
}

function nextPage() {
    if (currentPage.value < totalPages.value) currentPage.value++;
}

function resetForm() {
    form.value.routeName = "";
    form.value.routePath = "";
    form.value.parentRouteId = "";
    form.value.sortOrder = "";
}

async function loadRoutes() {
    try {
        const response = await axios.get(apiListUrl);
        routes.value = response?.data?.data ?? [];
    } catch (error) {
        console.error(error);
    }
}

async function submitForm() {
    try {
        const payload = new FormData();
        payload.append("RouteName", form.value.routeName);
        payload.append("RoutePath", form.value.routePath);
        payload.append("ParentRouteId", form.value.parentRouteId || "");
        payload.append("SortOrder", form.value.sortOrder || "");

        const response = await axios.post(apiCreateUrl, payload, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        alert(response?.data?.message || "Route created successfully.");
        resetForm();
        await loadRoutes();
    } catch (error) {
        alert(error?.response?.data?.message || error.message);
    }
}

onMounted(loadRoutes);
</script>
